import React from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { ProtectedRoute } from './routes/ProtectedRoute';
import { LoginPage } from './pages/LoginPage';
import { CallbackPage } from './pages/CallbackPage';
import { CreateCommunityPage } from './pages/CreateCommunityPage';
import { CommunitiesListPage } from './pages/CommunitiesListPage';
import { CommunityDetailPage } from './pages/CommunityDetailPage';
import { GroupDetailPage } from './pages/GroupDetailPage';
import { InterestsOverviewPage } from './pages/InterestsOverviewPage';
import { CreateInterestPage, CreateInterestTypePage } from './pages/CreateInterestPages';
import { CommunityDashboardPage } from './pages/CommunityDashboardPage';
import { BookingsListPage } from './pages/BookingsListPage';
import { BookingDetailsPage } from './pages/BookingDetailsPage';
import { RidesListPage } from './pages/RidesListPage';
import { EventsAdminPage } from './pages/EventsAdminPage';
import { RideDetailsPage } from './pages/RideDetailsPage';
import { RideRequestsListPage } from './pages/RideRequestsListPage';
import { UserLocationsListPage } from './pages/UserLocationsListPage';
import { LocationEditPage } from './pages/LocationEditPage';
import { LocationCreatePage } from './pages/LocationCreatePage';
import { NotificationsPage } from './pages/NotificationsPage';
import { CampaignEditorPage } from './pages/CampaignEditorPage';
import { CampaignDetailsPage } from './pages/CampaignDetailsPage';
import { AgentAssistantPage } from './pages/AgentAssistantPage';
import { PayoutsPage } from './pages/PayoutsPage';
import { AdminUsersPage } from './pages/AdminUsersPage';
import { AdminUserDetailsPage } from './pages/AdminUserDetailsPage';
import { PaymentOpsPage } from './pages/PaymentOpsPage';
import { LeadsPage } from './pages/LeadsPage';
import { AnalyticsDashboardPage } from './pages/AnalyticsDashboardPage';
import { CommunicationDashboardPage } from './pages/CommunicationDashboardPage';
import { useAuth } from './auth/AuthProvider';
import { useI18n } from './i18n/I18nProvider';

function App() {
  const { authenticated, logout } = useAuth();
  const { t, language, setLanguage } = useI18n();
  const location = useLocation();
  const [collapsed, setCollapsed] = React.useState(false);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const drawerRef = React.useRef<HTMLDialogElement>(null);
  const menuTriggerRef = React.useRef<HTMLButtonElement>(null);

  const closeNavigation = React.useCallback(() => {
    if (drawerRef.current?.open) drawerRef.current.close();
  }, []);

  React.useEffect(() => {
    closeNavigation();
  }, [location.pathname, closeNavigation]);

  React.useEffect(() => {
    const desktop = window.matchMedia('(min-width: 768px)');
    const onResize = () => {
      if (desktop.matches) closeNavigation();
    };
    desktop.addEventListener('change', onResize);
    return () => desktop.removeEventListener('change', onResize);
  }, [closeNavigation]);

  const navItems = [
    { path: '/dashboard', label: t('nav.dashboard'), icon: 'M3 13h8V3H3v10zm10 8h8V11h-8v10zM3 21h8v-6H3v6zm10-12h8V3h-8v6z' },
    { path: '/communities', label: t('nav.communities'), icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z' },
    { path: '/interests', label: t('nav.interests'), icon: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.518 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.364 1.118l1.519 4.674c.299.921-.756 1.688-1.54 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.539-1.118l1.518-4.674a1 1 0 00-.364-1.118L3.07 10.101c-.783-.57-.38-1.81.588-1.81h4.916a1 1 0 00.95-.69l1.525-4.674z' },
    { path: '/rides', label: t('nav.rides'), icon: 'M8 17l4 4 4-4m-4 4V3m-7 7h14M5 10a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v2a2 2 0 01-2 2' },
    { path: '/events', label: t('nav.events'), icon: 'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z' },
    { path: '/payouts', label: t('nav.payouts'), icon: 'M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z' },
    { path: '/payment-ops', label: t('nav.paymentOps'), icon: 'M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 10v2m8-6a8 8 0 11-16 0 8 8 0 0116 0z' },
    { path: '/users', label: t('nav.users'), icon: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z' },
    { path: '/leads', label: t('nav.leads'), icon: 'M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z' },
    { path: '/assistant', label: t('nav.aiAssistant'), icon: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z' },
    { path: '/notifications', label: t('nav.notifications'), icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
    { path: '/communications', label: t('shell.lifecycle'), icon: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8m-18 8h18V6H3v10z' },
  ];

  const navGroups = [
    { key: 'overview', paths: ['/dashboard'] },
    { key: 'operations', paths: ['/rides', '/events', '/payment-ops', '/payouts'] },
    { key: 'people', paths: ['/users', '/communities', '/interests', '/leads'] },
    { key: 'communication', paths: ['/notifications', '/communications'] },
    { key: 'tools', paths: ['/assistant'] },
  ];
  const isActive = (path: string) => location.pathname === path || location.pathname.startsWith(`${path}/`);
  const activeItem = navItems.find((item) => isActive(item.path));
  const activeGroup = navGroups.find((group) => group.paths.includes(activeItem?.path || ''));
  const collapseLabel = t(collapsed ? 'shell.expandNavigation' : 'shell.collapseNavigation');

  const renderBrand = (minimized: boolean) => (
    <Link className="admin-brand" to="/dashboard" aria-label="mobie portal" title={minimized ? 'mobie portal' : undefined}>
      <span className="admin-brand-name" aria-hidden="true">{minimized ? 'm' : 'mobie'}</span>
      {!minimized && <span className="admin-brand-caption" aria-hidden="true">portal</span>}
    </Link>
  );

  const renderNavigation = (minimized: boolean, surface: 'desktop' | 'mobile') => (
    <nav aria-label={t('shell.navigation')}>
      {navGroups.map((group) => (
        <section className="admin-nav-group" key={group.key} aria-labelledby={`${surface}-nav-${group.key}`}>
          <h2 id={`${surface}-nav-${group.key}`} className={`admin-nav-heading${minimized ? ' sr-only' : ''}`}>
            {t(`shell.group.${group.key}`)}
          </h2>
          {group.paths.map((path) => {
            const item = navItems.find((entry) => entry.path === path)!;
            return (
              <Link
                key={item.path}
                to={item.path}
                className="admin-nav-link"
                aria-current={isActive(item.path) ? 'page' : undefined}
                aria-label={minimized ? item.label : undefined}
                title={minimized ? item.label : undefined}
                onClick={surface === 'mobile' ? closeNavigation : undefined}
              >
                <svg className="admin-nav-icon" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d={item.icon} />
                </svg>
                {!minimized && <span>{item.label}</span>}
              </Link>
            );
          })}
        </section>
      ))}
    </nav>
  );

  return (
    <div className={`admin-app${collapsed ? ' admin-sidebar-collapsed' : ''}`}>
      <aside className="admin-sidebar">
        {renderBrand(collapsed)}
        {renderNavigation(collapsed, 'desktop')}
        <div className="admin-sidebar-footer">
          <button
            type="button"
            className="admin-sidebar-toggle"
            onClick={() => setCollapsed(!collapsed)}
            aria-label={collapseLabel}
            aria-expanded={!collapsed}
            title={collapseLabel}
          >
            <svg className="admin-nav-icon" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d={collapsed ? 'M9 5l7 7-7 7' : 'M15 5l-7 7 7 7'} />
            </svg>
            {!collapsed && <span>{t('shell.collapse')}</span>}
          </button>
        </div>
      </aside>
      <dialog
        ref={drawerRef}
        id="admin-mobile-navigation"
        className="admin-mobile-drawer"
        aria-label={t('shell.navigation')}
        onClose={() => {
          setDrawerOpen(false);
          menuTriggerRef.current?.focus();
        }}
        onClick={(event) => {
          if (event.target === event.currentTarget) {
            const bounds = event.currentTarget.getBoundingClientRect();
            if (event.clientX < bounds.left || event.clientX > bounds.right || event.clientY < bounds.top || event.clientY > bounds.bottom) closeNavigation();
          }
        }}
      >
        <div className="admin-mobile-drawer-header">
          {renderBrand(false)}
          <button type="button" className="admin-mobile-close" onClick={closeNavigation} aria-label={t('shell.closeNavigation')}>
            <svg className="admin-nav-icon" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d="M6 6l12 12M6 18L18 6" />
            </svg>
          </button>
        </div>
        {renderNavigation(false, 'mobile')}
      </dialog>
      <main className="admin-main">
        <header className="admin-topbar">
          <div className="admin-topbar-context">
            <button
              type="button"
              ref={menuTriggerRef}
              className="admin-mobile-trigger"
              aria-label={t('shell.openNavigation')}
              aria-expanded={drawerOpen}
              aria-controls="admin-mobile-navigation"
              onClick={() => {
                drawerRef.current?.showModal();
                setDrawerOpen(true);
              }}
            >
              <svg className="admin-nav-icon" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
            <span className="admin-topbar-section">{t(activeGroup ? `shell.group.${activeGroup.key}` : 'shell.workspace')}</span>
            <span className="admin-topbar-page">{activeItem?.label || t('app.brand')}</span>
          </div>
          <div className="admin-topbar-actions">
            <div className="admin-language-switch" role="group" aria-label={t('shell.language')}>
              <button type="button" onClick={() => setLanguage('de')} aria-pressed={language === 'de'} aria-label={t('shell.languageGerman')} lang="de">DE</button>
              <button type="button" onClick={() => setLanguage('en')} aria-pressed={language === 'en'} aria-label={t('shell.languageEnglish')} lang="en">EN</button>
            </div>
            {authenticated ? (
              <button type="button" className="admin-session-button" onClick={() => logout()}>
                <svg className="admin-nav-icon" aria-hidden="true" focusable="false" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                <span>{t('action.logout')}</span>
              </button>
            ) : (
              <Link className="btn btn-primary" to="/login">{t('action.login')}</Link>
            )}
          </div>
        </header>
        <div>
        <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/callback" element={<CallbackPage />} />
        <Route element={<ProtectedRoute />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<AnalyticsDashboardPage />} />
          <Route path="/communities" element={<CommunitiesListPage />} />
          <Route path="/communities/:id" element={<CommunityDetailPage />} />
          <Route path="/communities/:id/dashboard" element={<CommunityDashboardPage />} />
          <Route path="/communities/:communityId/groups/:groupId" element={<GroupDetailPage />} />
          <Route path="/communities/new" element={<CreateCommunityPage />} />
          <Route path="/interests" element={<InterestsOverviewPage />} />
          <Route path="/interests/new-type" element={<CreateInterestTypePage />} />
          <Route path="/interests/new" element={<CreateInterestPage />} />
          <Route path="/rides" element={<RidesListPage />} />
          <Route path="/rides/bookings" element={<BookingsListPage />} />
          <Route path="/rides/bookings/:bookingId" element={<BookingDetailsPage />} />
          <Route path="/events" element={<EventsAdminPage />} />
          <Route path="/rides/:id" element={<RideDetailsPage />} />
          <Route path="/ride-requests" element={<RideRequestsListPage />} />
          <Route path="/locations" element={<UserLocationsListPage />} />
          <Route path="/locations/new" element={<LocationCreatePage />} />
          <Route path="/locations/:id/edit" element={<LocationEditPage />} />
          <Route path="/payouts" element={<PayoutsPage />} />
          <Route path="/payment-ops" element={<PaymentOpsPage />} />
          <Route path="/users" element={<AdminUsersPage />} />
          <Route path="/users/:userId" element={<AdminUserDetailsPage />} />
          <Route path="/leads" element={<LeadsPage />} />
          <Route path="/assistant" element={<AgentAssistantPage />} />
          <Route path="/notifications" element={<NotificationsPage />} />
          <Route path="/notifications/new" element={<CampaignEditorPage />} />
          <Route path="/notifications/:campaignId/edit" element={<CampaignEditorPage />} />
          <Route path="/notifications/:campaignId" element={<CampaignDetailsPage />} />
          <Route path="/communications" element={<CommunicationDashboardPage />} />
        </Route>
        </Routes>
        </div>
      </main>
    </div>
  );
}

export default App;
